/**
 * @description
 * ClientUsersTab component for managing users within a specific client.
 * Embedded in ClientDetail page, shows user list and management functionality.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 6, 2025
 */

import React, { useState } from 'react';
import { Plus, Users, Search, RefreshCw, Key, Edit, Trash2 } from 'lucide-react';
import { useClientUsers } from '../../hooks/useClientUsers';
import { toast } from 'sonner';
import { UserManagementModal } from './UserManagementModal';
import { ApiKeyManagementModal } from './ApiKeyManagementModal';
import { ClientUser, ApiKey } from '../../services/api';

// Types
interface ClientUsersTabProps {
  clientId: string;
  clientName: string;
}

const ClientUsersTab: React.FC<ClientUsersTabProps> = ({ clientId, clientName }) => {
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ClientUser | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    users,
    loading,
    error,
    pagination,
    refetch,
    deleteUser,
    searchUsers,
  } = useClientUsers(clientId);

  const handleAddUser = (_user: ClientUser) => {
    toast.success('User added successfully');
    refetch();
  };

  const handleUpdateUser = (_user: ClientUser) => {
    toast.success('User updated successfully');
    refetch();
  };

  const handleDeleteUser = async (userId: string) => {
    if (confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(userId);
        toast.success('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const handleEditUser = (user: ClientUser) => {
    setSelectedUser(user);
    setShowEditUserModal(true);
  };

  const handleManageApiKeys = (user: ClientUser) => {
    setSelectedUser(user);
    setShowApiKeyModal(true);
  };

  const handleApiKeyAdded = (_key: ApiKey) => {
    toast.success('API key added successfully');
    refetch();
  };

  const handleApiKeyDeleted = (_keyId: string) => {
    toast.success('API key deleted successfully');
    refetch();
  };

  // Handle search
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchUsers]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-semibold text-foreground">
            Users for {clientName}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refetch}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setShowAddUserModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search users by email or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground"
        />
      </div>

      {/* Users Table */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : error ? (
          <div className="p-6 text-center text-destructive">
            Error loading users: {error}
          </div>
        ) : users.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">
            No users found for this client.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    API Keys
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-accent">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {user.userName}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {user.userEmail}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.role === 'owner' 
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : user.role === 'manager'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.status === 'active' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : user.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {user.apiKeysCount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleManageApiKeys(user)}
                          className="text-primary hover:text-primary/80 flex items-center gap-1"
                        >
                          <Key className="h-4 w-4" />
                          <span>Keys</span>
                        </button>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-muted-foreground hover:text-foreground flex items-center gap-1"
                        >
                          <Edit className="h-4 w-4" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-destructive hover:text-destructive/80 flex items-center gap-1"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => refetch({ page: pagination.page - 1 })}
                disabled={pagination.page === 1}
                className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => refetch({ page: pagination.page + 1 })}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <UserManagementModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        clientId={clientId}
        user={null}
        onUserAdded={handleAddUser}
        onUserUpdated={() => {}}
      />

      <UserManagementModal
        isOpen={showEditUserModal}
        onClose={() => {
          setShowEditUserModal(false);
          setSelectedUser(null);
        }}
        clientId={clientId}
        user={selectedUser}
        onUserAdded={() => {}}
        onUserUpdated={handleUpdateUser}
      />

      {selectedUser && (
        <ApiKeyManagementModal
          isOpen={showApiKeyModal}
          onClose={() => {
            setShowApiKeyModal(false);
            setSelectedUser(null);
          }}
          clientId={clientId}
          user={selectedUser}
          onKeyAdded={handleApiKeyAdded}
          onKeyDeleted={handleApiKeyDeleted}
        />
      )}
    </div>
  );
};

export default ClientUsersTab;